import { CSSProperties, useMemo } from 'react';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { MenuItem, makeStyles } from '@material-ui/core';
import pluralize from 'pluralize';

import { WbSelectInlineDivider, WbSelect } from '@agilelab/plugin-wb-platform';
import {
  ALL_TAXONOMIES_FILTER,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

type SearchTaxonomySelectorProps = {
  disabled?: boolean;
  onChange?: (e: string) => void;
  localStorageKey?: string;
  queryParamKey?: string;
  showLabel?: boolean;
  className?: string;
  style?: CSSProperties;
  inputHeight?: string | number;
};

const useStyles = makeStyles(theme => {
  return {
    inputStyle: {
      minHeight: 'auto',
      backgroundColor: 'white',
      borderRadius: '4px 0px 0px 4px',
      border: 'none',
      height: '52px',

      [theme.breakpoints.down('xs')]: {
        height: '40px',
        width: '100%',
        borderRadius: '4px',
      },
    },
  };
});

export const SearchTaxonomySelector = ({
  onChange,
  disabled,
  localStorageKey,
  queryParamKey,
  showLabel = true,
  className,
  style,
}: SearchTaxonomySelectorProps) => {
  const options = useMemo(
    () => ({ localStorageKey, queryParamKey }),
    [localStorageKey, queryParamKey],
  );
  const configApi = useApi(configApiRef);
  const classes = useStyles();

  const {
    selectableTaxonomies,
    selectedTaxonomyRef,
    setSelectedTaxonomyRef,
    shouldDisable,
  } = useTaxonomySelection(options);

  const showAllTaxonomyOption = selectableTaxonomies.length !== 1;

  const label = configApi.getString('practiceShaper.taxonomy.label');
  const pluralizedLabel =
    configApi.getOptionalString('practiceShaper.taxonomy.pluralizedLabel') ??
    pluralize(label);

  return (
    <WbSelect
      label={showLabel ? `${label}` : ''}
      id="data-landscape"
      value={selectedTaxonomyRef || ALL_TAXONOMIES_FILTER}
      disabled={disabled || shouldDisable}
      onChange={e => {
        const value = e.target.value as string;
        setSelectedTaxonomyRef(value);
        if (onChange) onChange(value);
      }}
      clearable={false}
      className={className}
      InputProps={{
        classes: {
          root: classes.inputStyle,
        },
      }}
      fullWidth
      style={{
        minHeight: 'auto',
        ...style,
        width: '100%',
      }}
    >
      {showAllTaxonomyOption && (
        <MenuItem value={ALL_TAXONOMIES_FILTER}>All {pluralizedLabel}</MenuItem>
      )}
      {showAllTaxonomyOption && (
        <WbSelectInlineDivider style={{ marginBlock: '8px' }} />
      )}
      {selectableTaxonomies.map(t => (
        <MenuItem key={t.metadata.name} value={stringifyEntityRef(t)}>
          {(t.metadata.displayName as string) || t.metadata.name}
        </MenuItem>
      ))}
    </WbSelect>
  );
};
